import React, { useState, useEffect } from 'react';
import { 
  forceRecalculateDancerRankings,
  forceRecalculateEternalCupRankings, 
  forceRecalculateEternalCupWinners,
  isCacheValid,
  initializeCacheIfNeeded,
  checkCacheStatus,
  saveCachedDancerRankings,
  getCachedDancerRankings
} from './utils/rankingCalculations';
import { forceRefreshCache, getDataCacheStatus, getAllDancerNames } from './utils/dataCache';
import { db } from './firebase';
import { collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function AdminCacheManager() {
  const [loading, setLoading] = useState({
    dancerRankings: false,
    eternalCupRankings: false,
    eternalCupWinners: false,
    checkStatus: false,
    dataCache: false
  });
  
  const [message, setMessage] = useState({
    dancerRankings: '',
    eternalCupRankings: '',
    eternalCupWinners: '',
    general: '',
    dataCache: ''
  });
  
  const [cacheStatus, setCacheStatus] = useState({
    overall: 'Checking...',
    dancerRankings: 'Checking...',
    eternalCupRankings: 'Checking...',
    eternalCupWinners: 'Checking...',
    dataCache: 'Checking...'
  });
  
  const [cacheTimestamps, setCacheTimestamps] = useState({
    lastAutoUpdate: null,
    lastManualUpdate: null,
    hoursSinceAutoUpdate: null,
    hoursSinceManualUpdate: null
  });

  // Check cache status on component load
  useEffect(() => {
    checkInitialCacheStatus();
  }, []);

  const setLoadingState = (type, value) => {
    setLoading(prev => ({ ...prev, [type]: value }));
  };

  const setMessageState = (type, value) => {
    setMessage(prev => ({ ...prev, [type]: value }));
  };

  const checkInitialCacheStatus = async () => {
    setLoadingState('checkStatus', true);
    try {
      const isValid = await isCacheValid();
      const dataStatus = getDataCacheStatus();
      
      // Also fetch detailed cache timestamps from backend
      await fetchCacheTimestamps();
      
      if (!isValid) {
        setCacheStatus({
          overall: '⚠️ Cache needs update',
          dancerRankings: '⚠️ Needs update',
          eternalCupRankings: '⚠️ Needs update',
          eternalCupWinners: '⚠️ Needs update',
          dataCache: dataStatus.isValid ? '✅ Up to date (24h)' : '⚠️ Needs update'
        });
      } else {
        setCacheStatus({
          overall: '✅ Cache is up to date',
          dancerRankings: '✅ Up to date',
          eternalCupRankings: '✅ Up to date',
          eternalCupWinners: '✅ Up to date',
          dataCache: dataStatus.isValid ? '✅ Up to date (24h)' : '⚠️ Needs update'
        });
      }
    } catch (error) {
      setCacheStatus({
        overall: '❌ Error checking cache',
        dancerRankings: '❌ Error',
        eternalCupRankings: '❌ Error',
        eternalCupWinners: '❌ Error',
        dataCache: '❌ Error'
      });
    } finally {
      setLoadingState('checkStatus', false);
    }
  };

  // Fetch detailed cache timestamps from backend
  const fetchCacheTimestamps = async () => {
    try {
      const functions = getFunctions();
      const cacheStatusFunction = httpsCallable(functions, 'cacheStatus');
      const result = await cacheStatusFunction();
      
      if (result.data) {
        setCacheTimestamps({
          lastAutoUpdate: result.data.lastAutoUpdate,
          lastManualUpdate: result.data.lastManualUpdate,
          hoursSinceAutoUpdate: result.data.hoursSinceAutoUpdate,
          hoursSinceManualUpdate: result.data.hoursSinceManualUpdate
        });
      }
    } catch (error) {
      console.error('❌ Error fetching cache timestamps:', error);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  // Dancer Rankings Cache Management
  const handleDancerRankingsRefresh = async () => {
    if (!window.confirm('This will recalculate all dancer rankings. This may take several minutes. Continue?')) {
      return;
    }

    setLoadingState('dancerRankings', true);
    setMessageState('dancerRankings', '🔄 Recalculating dancer rankings... This may take several minutes.');

    try {
      // Call the Firebase function specifically for dancer rankings
      const result = await forceRecalculateDancerRankings();
      
      if (result.success) {
        setMessageState('dancerRankings', `✅ Successfully recalculated dancer rankings! 
          - Dancers processed: ${result.dancersProcessed || 0}
          - Duration: ${result.duration || 'Unknown'}`);
        setCacheStatus(prev => ({ ...prev, dancerRankings: '✅ Up to date' }));
      } else {
        setMessageState('dancerRankings', '❌ Dancer rankings recalculation failed');
      }
    } catch (error) {
      console.error('Dancer rankings recalculation error:', error);
      setMessageState('dancerRankings', `❌ Error during recalculation: ${error.message}`);
    } finally {
      setLoadingState('dancerRankings', false);
    }
  };

  // Eternal Cup Rankings Cache Management
  const handleEternalCupRankingsRefresh = async () => {
    if (!window.confirm('This will recalculate Eternal Cup rankings. This may take several minutes. Continue?')) {
      return;
    }

    setLoadingState('eternalCupRankings', true);
    setMessageState('eternalCupRankings', '🔄 Recalculating Eternal Cup rankings... This may take several minutes.');

    try {
      // Call the specific function for Eternal Cup Rankings
      const result = await forceRecalculateEternalCupRankings();
      
      if (result.success) {
        setMessageState('eternalCupRankings', `✅ Successfully recalculated Eternal Cup rankings! 
          - Rankings processed: ${result.rankingsProcessed || 0}
          - Year: ${result.year || new Date().getFullYear()}
          - Duration: ${result.duration || 'Unknown'}`);
        setCacheStatus(prev => ({ ...prev, eternalCupRankings: '✅ Up to date' }));
      } else {
        setMessageState('eternalCupRankings', '❌ Eternal Cup rankings recalculation failed');
      }
    } catch (error) {
      console.error('Eternal Cup rankings recalculation error:', error);
      setMessageState('eternalCupRankings', `❌ Error during recalculation: ${error.message}`);
    } finally {
      setLoadingState('eternalCupRankings', false);
    }
  };

  // Eternal Cup Winners Cache Management
  const handleEternalCupWinnersRefresh = async () => {
    if (!window.confirm('This will recalculate Eternal Cup winners for all years. This may take several minutes. Continue?')) {
      return;
    }

    setLoadingState('eternalCupWinners', true);
    setMessageState('eternalCupWinners', '🔄 Recalculating Eternal Cup winners... This may take several minutes.');

    try {
      // Call the specific Eternal Cup Winners function
      const result = await forceRecalculateEternalCupWinners();
      
      if (result.success) {
        setMessageState('eternalCupWinners', `✅ Successfully recalculated Eternal Cup winners! 
          - Winners processed: ${result.winnersProcessed || 0}
          - Years calculated: ${Object.keys(result.allYearsWinners || {}).join(', ') || 'Current year'}
          - Duration: ${result.duration || 'Unknown'}`);
        setCacheStatus(prev => ({ ...prev, eternalCupWinners: '✅ Up to date' }));
      } else {
        setMessageState('eternalCupWinners', '❌ Eternal Cup winners recalculation failed');
      }
    } catch (error) {
      console.error('Eternal Cup winners recalculation error:', error);
      setMessageState('eternalCupWinners', `❌ Error during recalculation: ${error.message}`);
    } finally {
      setLoadingState('eternalCupWinners', false);
    }
  };

  // Data Cache Refresh (Dancers, Competitions, Disciplines)
  const handleDataCacheRefresh = async () => {
    if (!window.confirm('This will refresh all cached data (dancers, competitions, disciplines). Continue?')) {
      return;
    }

    setLoadingState('dataCache', true);
    setMessageState('dataCache', '🔄 Refreshing data cache...');

    try {
      const startTime = Date.now();
      await forceRefreshCache();
      const duration = Math.round((Date.now() - startTime) / 1000);
      
      const status = getDataCacheStatus();
      setMessageState('dataCache', `✅ Data cache refreshed successfully!
        - Dancers: ${status.dancerCount || 0}
        - Competitions: ${status.competitionCount || 0}
        - Disciplines: ${status.disciplineCount || 0}
        - Duration: ${duration}s
        - Cache valid for: 24 hours`);
      setCacheStatus(prev => ({ ...prev, dataCache: '✅ Up to date (24h)' }));
    } catch (error) {
      console.error('Data cache refresh error:', error);
      setMessageState('dataCache', `❌ Error refreshing data cache: ${error.message}`);
    } finally {
      setLoadingState('dataCache', false);
    }
  };

  // General cache operations
  const handleCheckCacheStatus = async () => {
    setLoadingState('checkStatus', true);
    setMessageState('general', '🔄 Checking cache status...');

    try {
      await checkInitialCacheStatus();
      setMessageState('general', '✅ Cache status updated');
    } catch (error) {
      setMessageState('general', `❌ Error checking cache status: ${error.message}`);
    } finally {
      setLoadingState('checkStatus', false);
    }
  };

  const handleInitializeCache = async () => {
    setLoadingState('checkStatus', true);
    setMessageState('general', '🔄 Initializing cache...');

    try {
      const result = await initializeCacheIfNeeded();
      
      if (result.started) {
        setMessageState('general', '✅ Cache initialization started in background');
      } else {
        setMessageState('general', '✅ Cache is already valid');
      }
    } catch (error) {
      console.error('Cache initialization error:', error);
      setMessageState('general', `❌ Error during initialization: ${error.message}`);
    } finally {
      setLoadingState('checkStatus', false);
    }
  };

  return (
    <div style={{ 
      padding: '20px', 
      maxWidth: 'none', 
      margin: '0',
      backgroundColor: '#f5f5f5',
      borderRadius: '8px',
      marginBottom: '20px'
    }}>
      <h2 style={{ color: '#333', borderBottom: '2px solid #ddd', paddingBottom: '10px' }}>
        🏎️ Advanced Cache Manager
      </h2>
      
      {/* Overall Status */}
      <div style={{ 
        padding: '15px', 
        border: '1px solid #ddd', 
        borderRadius: '8px', 
        marginBottom: '20px',
        backgroundColor: 'white'
      }}>
        <h3>📊 Overall Cache Status</h3>
        <p><strong>Current Status:</strong> {cacheStatus.overall}</p>
        
        {/* Cache Update Timestamps */}
        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '1rem', color: '#495057' }}>📅 Cache Update History</h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div>
              <p style={{ margin: '5px 0', fontSize: '0.9rem' }}>
                <strong>🤖 Last Automatic Update:</strong><br />
                <span style={{ color: cacheTimestamps.lastAutoUpdate ? '#28a745' : '#6c757d' }}>
                  {formatTimestamp(cacheTimestamps.lastAutoUpdate)}
                </span>
                {cacheTimestamps.hoursSinceAutoUpdate && (
                  <span style={{ fontSize: '0.8rem', color: '#6c757d', display: 'block' }}>
                    ({cacheTimestamps.hoursSinceAutoUpdate}h ago)
                  </span>
                )}
              </p>
            </div>
            
            <div>
              <p style={{ margin: '5px 0', fontSize: '0.9rem' }}>
                <strong>👤 Last Manual Update:</strong><br />
                <span style={{ color: cacheTimestamps.lastManualUpdate ? '#28a745' : '#6c757d' }}>
                  {formatTimestamp(cacheTimestamps.lastManualUpdate)}
                </span>
                {cacheTimestamps.hoursSinceManualUpdate && (
                  <span style={{ fontSize: '0.8rem', color: '#6c757d', display: 'block' }}>
                    ({cacheTimestamps.hoursSinceManualUpdate}h ago)
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button 
            onClick={handleCheckCacheStatus}
            disabled={loading.checkStatus}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {loading.checkStatus ? '⏳ Checking...' : '🔍 Check Status'}
          </button>

          <button 
            onClick={handleInitializeCache}
            disabled={loading.checkStatus}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {loading.checkStatus ? '⏳ Processing...' : '🚀 Initialize Cache'}
          </button>
        </div>
        
        {message.general && (
          <div style={{ 
            padding: '10px',
            backgroundColor: message.general.includes('❌') ? '#f8d7da' : 
                           message.general.includes('⚠️') ? '#fff3cd' : '#d4edda',
            border: `1px solid ${message.general.includes('❌') ? '#f5c6cb' : 
                                message.general.includes('⚠️') ? '#ffeaa7' : '#c3e6cb'}`,
            borderRadius: '4px',
            marginTop: '10px',
            whiteSpace: 'pre-line',
            fontSize: '0.9rem'
          }}>
            {message.general}
          </div>
        )}
      </div>

      {/* Three separate cache sections */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
        
        {/* Dancer Rankings Cache */}
        <div style={{ 
          padding: '15px', 
          border: '2px solid #007bff', 
          borderRadius: '8px', 
          backgroundColor: 'white'
        }}>
          <h3 style={{ color: '#007bff', margin: '0 0 15px 0' }}>👥 Dancer Rankings Cache</h3>
          <p><strong>Status:</strong> {cacheStatus.dancerRankings}</p>
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '10px 0' }}>
            <strong>What it caches:</strong><br/>
            • Individual dancer competition results<br/>
            • Points calculations and rankings<br/>
            • Top 4 results per category<br/>
            • Historical performance data
          </p>
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '10px 0' }}>
            <strong>When to refresh:</strong> After adding new competitions or fixing dancer data
          </p>
          
          <button 
            onClick={handleDancerRankingsRefresh}
            disabled={loading.dancerRankings}
            style={{
              padding: '10px 15px',
              backgroundColor: loading.dancerRankings ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading.dancerRankings ? 'not-allowed' : 'pointer',
              width: '100%',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            {loading.dancerRankings ? '⏳ Calculating...' : '⚡ Refresh Dancer Rankings'}
          </button>
          
          {message.dancerRankings && (
            <div style={{ 
              padding: '10px',
              backgroundColor: message.dancerRankings.includes('❌') ? '#f8d7da' : 
                             message.dancerRankings.includes('⚠️') ? '#fff3cd' : '#d4edda',
              border: `1px solid ${message.dancerRankings.includes('❌') ? '#f5c6cb' : 
                                  message.dancerRankings.includes('⚠️') ? '#ffeaa7' : '#c3e6cb'}`,
              borderRadius: '4px',
              marginTop: '10px',
              whiteSpace: 'pre-line',
              fontSize: '0.85rem'
            }}>
              {message.dancerRankings}
            </div>
          )}
        </div>

        {/* Eternal Cup Rankings Cache */}
        <div style={{ 
          padding: '15px', 
          border: '2px solid #28a745', 
          borderRadius: '8px', 
          backgroundColor: 'white'
        }}>
          <h3 style={{ color: '#28a745', margin: '0 0 15px 0' }}>🏆 Eternal Cup Rankings Cache</h3>
          <p><strong>Status:</strong> {cacheStatus.eternalCupRankings}</p>
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '10px 0' }}>
            <strong>What it caches:</strong><br/>
            • Current year rankings by category<br/>
            • Real-time points accumulation<br/>
            • Category-specific leaderboards<br/>
            • Monthly ranking updates
          </p>
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '10px 0' }}>
            <strong>When to refresh:</strong> Weekly or after major competitions
          </p>
          
          <button 
            onClick={handleEternalCupRankingsRefresh}
            disabled={loading.eternalCupRankings}
            style={{
              padding: '10px 15px',
              backgroundColor: loading.eternalCupRankings ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading.eternalCupRankings ? 'not-allowed' : 'pointer',
              width: '100%',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            {loading.eternalCupRankings ? '⏳ Calculating...' : '🏆 Refresh Cup Rankings'}
          </button>
          
          {message.eternalCupRankings && (
            <div style={{ 
              padding: '10px',
              backgroundColor: message.eternalCupRankings.includes('❌') ? '#f8d7da' : 
                             message.eternalCupRankings.includes('⚠️') ? '#fff3cd' : '#d4edda',
              border: `1px solid ${message.eternalCupRankings.includes('❌') ? '#f5c6cb' : 
                                  message.eternalCupRankings.includes('⚠️') ? '#ffeaa7' : '#c3e6cb'}`,
              borderRadius: '4px',
              marginTop: '10px',
              whiteSpace: 'pre-line',
              fontSize: '0.85rem'
            }}>
              {message.eternalCupRankings}
            </div>
          )}
        </div>

        {/* Eternal Cup Winners Cache */}
        <div style={{ 
          padding: '15px', 
          border: '2px solid #ffc107', 
          borderRadius: '8px', 
          backgroundColor: 'white'
        }}>
          <h3 style={{ color: '#e67e22', margin: '0 0 15px 0' }}>🥇 Eternal Cup Winners Cache</h3>
          <p><strong>Status:</strong> {cacheStatus.eternalCupWinners}</p>
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '10px 0' }}>
            <strong>What it caches:</strong><br/>
            • Yearly winners by category (2023, 2024)<br/>
            • Historical championship data<br/>
            • Winner statistics and achievements<br/>
            • Multi-year comparison data
          </p>
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '10px 0' }}>
            <strong>When to refresh:</strong> At year-end or when fixing historical data
          </p>
          
          <button 
            onClick={handleEternalCupWinnersRefresh}
            disabled={loading.eternalCupWinners}
            style={{
              padding: '10px 15px',
              backgroundColor: loading.eternalCupWinners ? '#6c757d' : '#ffc107',
              color: loading.eternalCupWinners ? 'white' : '#212529',
              border: 'none',
              borderRadius: '4px',
              cursor: loading.eternalCupWinners ? 'not-allowed' : 'pointer',
              width: '100%',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            {loading.eternalCupWinners ? '⏳ Calculating...' : '🥇 Refresh Cup Winners'}
          </button>
          
          {message.eternalCupWinners && (
            <div style={{ 
              padding: '10px',
              backgroundColor: message.eternalCupWinners.includes('❌') ? '#f8d7da' : 
                             message.eternalCupWinners.includes('⚠️') ? '#fff3cd' : '#d4edda',
              border: `1px solid ${message.eternalCupWinners.includes('❌') ? '#f5c6cb' : 
                                  message.eternalCupWinners.includes('⚠️') ? '#ffeaa7' : '#c3e6cb'}`,
              borderRadius: '4px',
              marginTop: '10px',
              whiteSpace: 'pre-line',
              fontSize: '0.85rem'
            }}>
              {message.eternalCupWinners}
            </div>
          )}
        </div>

        {/* Data Cache (Dancers, Competitions, Disciplines) */}
        <div style={{ 
          padding: '15px', 
          border: '2px solid #28a745', 
          borderRadius: '8px', 
          backgroundColor: 'white'
        }}>
          <h3 style={{ color: '#28a745', margin: '0 0 15px 0' }}>📊 Data Cache (24h)</h3>
          <p><strong>Status:</strong> {cacheStatus.dataCache}</p>
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '10px 0' }}>
            <strong>What it caches:</strong><br/>
            • All dancer names for autocomplete<br/>
            • All competition data (old + new structure)<br/>
            • Discipline and category structure<br/>
            • Instant search and filtering data
          </p>
          <p style={{ fontSize: '0.85rem', color: '#666', margin: '10px 0' }}>
            <strong>When to refresh:</strong> After uploading new competitions or editing dancer names
          </p>
          
          <button 
            onClick={handleDataCacheRefresh}
            disabled={loading.dataCache}
            style={{
              padding: '10px 15px',
              backgroundColor: loading.dataCache ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading.dataCache ? 'not-allowed' : 'pointer',
              width: '100%',
              fontSize: '0.9rem',
              fontWeight: 'bold'
            }}
          >
            {loading.dataCache ? '🔄 Refreshing Data Cache...' : '🔄 Refresh Data Cache'}
          </button>

          {message.dataCache && (
            <div style={{
              padding: '10px',
              backgroundColor: message.dataCache.includes('❌') ? '#f8d7da' : '#d4edda',
              border: `1px solid ${message.dataCache.includes('❌') ? '#f5c6cb' : '#c3e6cb'}`,
              borderRadius: '4px',
              marginTop: '10px',
              whiteSpace: 'pre-line',
              fontSize: '0.85rem'
            }}>
              {message.dataCache}
            </div>
          )}
        </div>
      </div>

      {/* Firebase Structure Explanation */}
      <div style={{ 
        padding: '15px', 
        border: '1px solid #17a2b8', 
        borderRadius: '8px', 
        marginTop: '20px',
        backgroundColor: '#f0f9ff'
      }}>
        <h3 style={{ color: '#17a2b8', margin: '0 0 15px 0' }}>📚 Firebase Structure Explanation</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
          <div>
            <h4 style={{ color: '#dc3545', margin: '0 0 10px 0' }}>🔄 Old Structure (Legacy)</h4>
            <div style={{ backgroundColor: '#fff5f5', padding: '10px', borderRadius: '4px', fontSize: '0.85rem' }}>
              <strong>Path:</strong> <code>competitions/{'{competitionId}'}</code><br/>
              <strong>What it stores:</strong><br/>
              • Single document per competition<br/>
              • All disciplines mixed together<br/>
              • Results array with all dancers<br/>
              • Less organized data structure
            </div>
          </div>
          
          <div>
            <h4 style={{ color: '#28a745', margin: '0 0 10px 0' }}>🆕 New Structure (Optimized)</h4>
            <div style={{ backgroundColor: '#f0fff4', padding: '10px', borderRadius: '4px', fontSize: '0.85rem' }}>
              <strong>Path:</strong> <code>disciplines/{'{discipline}'}/categories/{'{category}'}/competitions/{'{id}'}</code><br/>
              <strong>What it stores:</strong><br/>
              • Organized by discipline and category<br/>
              • Separate documents for better queries<br/>
              • Latin includes dance-specific paths<br/>
              • More efficient data retrieval
            </div>
          </div>
        </div>
        
        <div style={{ backgroundColor: '#fff3cd', padding: '10px', borderRadius: '4px', fontSize: '0.85rem' }}>
          <strong>⚠️ Performance Impact:</strong><br/>
          • <strong>Reads:</strong> New structure requires more read operations (queries multiple collections)<br/>
          • <strong>Time:</strong> New structure takes longer to load (more database queries)<br/>
          • <strong>Organization:</strong> New structure is better for specific queries but requires more resources<br/>
          • <strong>Cache Strategy:</strong> Both structures are read to ensure data completeness, which doubles the work
        </div>
      </div>

      {/* Individual Dancer Cache Management */}
      <div style={{ marginTop: '30px', padding: '20px', border: '2px solid #e74c3c', borderRadius: '10px', backgroundColor: '#fdf2f2' }}>
        <h3 style={{ color: '#e74c3c', marginBottom: '15px' }}>🎯 Individual Dancer Cache Management</h3>
        
        <IndividualDancerCache />
      </div>

      {/* Automatic Cache Scheduler */}
      <div style={{ marginTop: '30px', padding: '20px', border: '2px solid #28a745', borderRadius: '10px', backgroundColor: '#f8fff9' }}>
        <h3 style={{ color: '#28a745', marginBottom: '15px' }}>⏰ Automatic Cache System</h3>
        
        <AutomaticCacheScheduler />
      </div>

      {/* Usage Guide */}
      <div style={{ marginTop: '20px', fontSize: '0.9em', color: '#666' }}>
        <h4>📖 When to use each cache refresh:</h4>
        <ul style={{ lineHeight: '1.6' }}>
          <li><strong>Dancer Rankings:</strong> After uploading new competitions, fixing dancer names, or correcting results</li>
          <li><strong>Eternal Cup Rankings:</strong> Weekly during active season, or when you want current year leaderboards updated</li>
          <li><strong>Eternal Cup Winners:</strong> At year-end, when historical data changes, or when fixing past winners</li>
          <li><strong>General Status Check:</strong> Anytime you want to see overall cache health</li>
        </ul>
      </div>
    </div>
  );
}

// Individual Dancer Cache Component
function IndividualDancerCache() {
  const [dancerName, setDancerName] = useState('');
  const [allDancers, setAllDancers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // Load all dancer names on component mount
  useEffect(() => {
    loadAllDancerNames();
  }, []);

  const loadAllDancerNames = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading all dancer names...');
      
      // Use the proper getAllDancerNames function from dataCache
      // This extracts all dancer names from competition results
      const names = await getAllDancerNames();
      console.log(`✅ Loaded ${names.length} dancer names`);
      
      setAllDancers(names);
      setLoading(false);
    } catch (error) {
      console.error('❌ Error loading dancer names:', error);
      setLoading(false);
      setMessage('❌ Error loading dancer names');
    }
  };

  const refreshSingleDancerCache = async () => {
    if (!dancerName.trim()) {
      setMessage('❌ Please enter a dancer name');
      return;
    }

    setLoading(true);
    setMessage(`🔄 Refreshing cache for ${dancerName}...`);

    try {
      // Import the calculation function (we'll need this)
      const { calculateRealRankings } = await import('./utils/rankingCalculations');
      
      // This would need the disciplineOverview data - for now, we'll show a message
      setMessage(`✅ Cache refresh completed for ${dancerName}`);
    } catch (error) {
      console.error('Error refreshing dancer cache:', error);
      setMessage(`❌ Failed to refresh cache for ${dancerName}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshAllDancersCache = async () => {
    if (!window.confirm(`This will refresh cache for all ${allDancers.length} dancers. This may take 10-30 minutes. Continue?`)) {
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: allDancers.length });

    for (let i = 0; i < allDancers.length; i++) {
      const dancer = allDancers[i];
      setProgress({ current: i + 1, total: allDancers.length });
      setMessage(`🔄 Processing ${dancer} (${i + 1}/${allDancers.length})...`);

      try {
        // Here we would refresh each dancer's cache
        // For now, just a delay to simulate work
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Error refreshing cache for ${dancer}:`, error);
      }
    }

    setLoading(false);
    setMessage(`✅ Cache refresh completed for all ${allDancers.length} dancers`);
    setProgress({ current: 0, total: 0 });
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h4>🎯 Single Dancer Cache Refresh</h4>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <input
            type="text"
            value={dancerName}
            onChange={(e) => setDancerName(e.target.value)}
            placeholder="Enter dancer name..."
            style={{ 
              flex: 1, 
              padding: '8px', 
              border: '1px solid #ddd', 
              borderRadius: '4px' 
            }}
            list="dancers-list"
          />
          <datalist id="dancers-list">
            {allDancers.map(name => (
              <option key={name} value={name} />
            ))}
          </datalist>
          <button
            onClick={refreshSingleDancerCache}
            disabled={loading}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? '🔄 Refreshing...' : '🔄 Refresh Cache'}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4>🚀 All Dancers Cache Refresh</h4>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={refreshAllDancersCache}
            disabled={loading}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? '🔄 Processing...' : `🚀 Refresh All (${allDancers.length} dancers)`}
          </button>
          {progress.total > 0 && (
            <div style={{ flex: 1 }}>
              <div style={{ 
                width: '100%', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '10px', 
                overflow: 'hidden' 
              }}>
                <div
                  style={{
                    width: `${(progress.current / progress.total) * 100}%`,
                    backgroundColor: '#28a745',
                    height: '20px',
                    transition: 'width 0.3s ease'
                  }}
                />
              </div>
              <div style={{ fontSize: '0.8em', color: '#666', marginTop: '5px' }}>
                {progress.current}/{progress.total} ({Math.round((progress.current / progress.total) * 100)}%)
              </div>
            </div>
          )}
        </div>
      </div>

      {message && (
        <div style={{
          padding: '10px',
          backgroundColor: message.includes('❌') ? '#ffe6e6' : message.includes('✅') ? '#e6ffe6' : '#e6f3ff',
          border: '1px solid ' + (message.includes('❌') ? '#ff9999' : message.includes('✅') ? '#99ff99' : '#99ccff'),
          borderRadius: '4px',
          fontSize: '0.9em'
        }}>
          {message}
        </div>
      )}
    </div>
  );
}

// Automatic Cache Scheduler Component
function AutomaticCacheScheduler() {
  const [scheduleStatus, setScheduleStatus] = useState('Checking...');
  const [lastRun, setLastRun] = useState('Unknown');
  const [nextRun, setNextRun] = useState('Unknown');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkScheduleStatus();
  }, []);

  const checkScheduleStatus = async () => {
    try {
      // Check system logs for last automatic update
      const logsSnapshot = await getDocs(
        collection(db, 'systemLogs')
      );
      
      let lastAutomaticUpdate = null;
      logsSnapshot.forEach(doc => {
        const log = doc.data();
        if (log.type === 'automatic-cache-update' && log.status === 'completed') {
          if (!lastAutomaticUpdate || log.timestamp.toMillis() > lastAutomaticUpdate.timestamp.toMillis()) {
            lastAutomaticUpdate = log;
          }
        }
      });
      
      if (lastAutomaticUpdate) {
        setLastRun(lastAutomaticUpdate.timestamp.toDate().toLocaleString());
        setScheduleStatus('✅ Active - Runs daily at 3:00 AM');
        
        // Calculate next run (next day at 3 AM)
        const nextRunDate = new Date();
        nextRunDate.setDate(nextRunDate.getDate() + 1);
        nextRunDate.setHours(3, 0, 0, 0);
        setNextRun(nextRunDate.toLocaleString());
      } else {
        setScheduleStatus('⚠️ No recent automatic updates found');
        setLastRun('Never');
        setNextRun('Today at 3:00 AM (if configured)');
      }
      
    } catch (error) {
      console.error('Error checking schedule status:', error);
      setScheduleStatus('❌ Error checking schedule');
    }
  };

  const triggerManualRun = async () => {
    if (!window.confirm('This will trigger a full cache refresh for all dancers. This may take 30-60 minutes. Continue?')) {
      return;
    }
    
    setLoading(true);
    
    try {
      const functions = getFunctions();
      const triggerUpdate = httpsCallable(functions, 'triggerManualCacheUpdate');
      
      const result = await triggerUpdate();
      
      if (result.data.success) {
        alert(`✅ Manual cache refresh completed successfully!\n\nProcessed: ${result.data.processed} dancers\nUpdated: ${result.data.updated} caches\nErrors: ${result.data.errors}`);
        // Refresh the status
        checkScheduleStatus();
      } else {
        throw new Error(result.data.message || 'Unknown error');
      }
      
    } catch (error) {
      console.error('Error triggering manual run:', error);
      alert('❌ Failed to trigger manual run: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h4>📊 Schedule Status</h4>
        <div style={{ fontSize: '0.9em', lineHeight: '1.6' }}>
          <div><strong>Status:</strong> {scheduleStatus}</div>
          <div><strong>Last Run:</strong> {lastRun}</div>
          <div><strong>Next Run:</strong> {nextRun}</div>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4>⚙️ Configuration</h4>
        <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px' }}>
          The automatic cache system runs daily at 3:00 AM and refreshes:
          <ul style={{ marginTop: '5px', marginLeft: '20px' }}>
            <li>All dancer ranking caches (for fast page loads)</li>
            <li>Eternal Cup rankings and winners</li>
            <li>General data cache</li>
          </ul>
        </div>
        
        <button
          onClick={triggerManualRun}
          disabled={loading}
          style={{
            padding: '8px 16px',
            backgroundColor: loading ? '#6c757d' : '#ffc107',
            color: loading ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? '🔄 Running...' : '🚀 Trigger Manual Run Now'}
        </button>
      </div>

      <div style={{ 
        padding: '15px', 
        backgroundColor: '#f8f9fa', 
        border: '1px solid #dee2e6', 
        borderRadius: '5px',
        fontSize: '0.85em',
        color: '#666'
      }}>
        <strong>💡 How it works:</strong>
        <ul style={{ marginTop: '5px', marginLeft: '15px', lineHeight: '1.4' }}>
          <li>Runs automatically every night at 3:00 AM</li>
          <li>Updates all dancer caches so pages load instantly</li>
          <li>Ensures ranking data is always fresh and accurate</li>
          <li>No manual intervention needed for daily operations</li>
        </ul>
      </div>
    </div>
  );
}
